import MainIcon from '../../assets/icons/house-solid.svg';
import StatisticsIcon from '../../assets/icons/statistics-icon.svg';
import BusinessUnitIcon from '../../assets/icons/business-unit-icon.svg';
import BusinessStocksIcon from '../../assets/icons/warehouse.svg';
import ProductIcon from '../../assets/icons/products.svg';
import RolesIcon from '../../assets/icons/people-group-solid.svg';
import UserIcon from '../../assets/icons/user.svg';
import DollarIcon from '../../assets/icons/dollar-icon.svg';
import {PermissionGroup} from '../../utils/permission';
import ReportsIcon from '../../assets/icons/reports-icon.svg';
import Settings from '../../assets/icons/settings.svg';
import OnlineShopIcon from '../../assets/icons/online-shop.svg';
import CrmModuleIcon from '../../assets/icons/crm-module.svg';
import BulletPoint from '../../assets/icons/bullet.svg';
import GetCash from '../../assets/icons/cash.png';
import Installment from '../../assets/icons/installment.svg';
import MoneyBillIcon from '../../assets/icons/money-bill-solid.svg';
import IntegrationsIcon from '../../assets/icons/integration-hub.svg';
import DictionaryStore from "../../utils/dictionary/dictionary.store";
import {makeAutoObservable, runInAction, autorun} from "mobx";
import AppStore from "../../app.store";
import {ENABLE_BILLING} from "../../config/features";
import {hasPermission} from "../../utils/hasPermission";

interface NavItemsModel {
    label: string;
    route?: string;
    icon?: string;
    permission?: string;
    hidden?: boolean;
    children?: any[];
    id?: string;
}

const NavItemsInitialState: NavItemsModel[] = []

class LinksStore {
    public NavItems = NavItemsInitialState;
    public dictionary = DictionaryStore;
    public AppStore = AppStore;
    private navItemsInitialized = false;

    constructor() {
        makeAutoObservable(this);
        
        // Автоматически обновляем NavItems при изменении businessSettings
        autorun(() => {
            // Отслеживаем изменения businessSettings для реактивности
            const businessSettings = this.AppStore.businessSettings;
            if (this.navItemsInitialized && businessSettings) {
                this.updateNavItems();
            }
        });
    }

    public NavItemsReq = async (signal: AbortSignal) => {
        await this.dictionary.sidebarDictionaryReq(signal);
        
        runInAction(() => {
            this.navItemsInitialized = true;
        });
        
        this.updateNavItems();
    }

    private updateNavItems = () => {
        runInAction(() => {
            this.NavItems = [
                {
                    label: this.dictionary.getSidebarLabel('MAIN'),
                    id: 'main',
                    route: '/',
                    icon: MainIcon,
                    hidden: false,
                },
                {
                    label: this.dictionary.getSidebarLabel('TRANSACTIONS'),
                    id: 'transactions',
                    icon: StatisticsIcon,
                    permission: PermissionGroup.TRANSACTIONS,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('TRANSACTIONS'),
                            id: 'transactions_list',
                            route: '/transactions/list',
                            icon: BulletPoint,
                            permission: PermissionGroup.TRANSACTIONS,
                        },
                        {
                            label: 'Сервисы в транзакциях',
                            id: 'transactions_services',
                            route: '/transactions/services',
                            icon: BulletPoint,
                            permission: PermissionGroup.TRANSACTIONS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('ORDER_REQUESTS'),
                            id: 'orders',
                            route: '/transactions/orders',
                            icon: BulletPoint,
                            permission: PermissionGroup.ORDER_REQUESTS,
                        },
                    ]
                },
                {
                    label: this.dictionary.getSidebarLabel('BUSINESS UNITS'),
                    id: 'business_units',
                    route: '/business_units',
                    icon: BusinessUnitIcon,
                    permission: PermissionGroup.BUSINESS_UNITS,
                },
                {
                    label: 'Кассовые сессии',
                    id: 'cash_sessions',
                    route: '/cash-sessions',
                    icon: GetCash,
                    permission: PermissionGroup.CASHIER_SESSION,
                },
                {
                    label: this.dictionary.getSidebarLabel('PRODUCTS'),
                    id: 'products-dropdown',
                    icon: ProductIcon,
                    permission: PermissionGroup.PRODUCTS,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('PRODUCTS'),
                            id: 'products_list',
                            route: '/products/list',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('BRANDS'),
                            id: 'products_brands',
                            route: '/products/brands',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCT_BRANDS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('PRODUCT CATEGORIES'),
                            id: 'products_categories',
                            route: '/products/categories',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCT_CATEGORIES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('SUPPLIERS'),
                            id: 'products_suppliers',
                            route: '/products/suppliers',
                            icon: BulletPoint,
                            permission: PermissionGroup.SUPPLIERS,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('WAREHOUSE'),
                    id: 'business_stock-dropdown',
                    icon: BusinessStocksIcon,
                    permission: PermissionGroup.BUSINESS_STOCKS,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('BUSINESS STOCKS'),
                            id: 'business_stock_list',
                            route: '/business_stock/list',
                            icon: BulletPoint,
                            permission: PermissionGroup.BUSINESS_STOCKS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('TRANSFERS'),
                            id: 'business_stock_transfers',
                            route: '/business_stock/transfers',
                            icon: BulletPoint,
                            permission: PermissionGroup.TRANSFERS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('GOODS_MOVEMENTS'),
                            id: 'business_stock_movements',
                            route: '/business_stock/movements',
                            icon: BulletPoint,
                            permission: PermissionGroup.INVENTORY_ADJUSTMENTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('PURCHASE_BILLS'),
                            id: 'business_stock_bills',
                            route: '/business_stock/bills',
                            icon: BulletPoint,
                            permission: PermissionGroup.BUSINESS_STOCKS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('PURCHASE_BILLS_OPERATIONS'),
                            id: 'business_stock_bills_operations',
                            route: '/business_stock/bills_operations',
                            icon: BulletPoint,
                            permission: PermissionGroup.BUSINESS_STOCKS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('SHIPMENTS'),
                            id: 'business_stock_shipments',
                            route: '/business_stock/shipments',
                            icon: BulletPoint,
                            permission: PermissionGroup.INVENTORY_ADJUSTMENTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('INVENTORY ADJUSTMENTS'),
                            id: 'business_stock_adjustments',
                            route: '/business_stock/adjustments',
                            icon: BulletPoint,
                            permission: PermissionGroup.INVENTORY_ADJUSTMENTS,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('CLIENTS'),
                    id: 'clients-dropdown',
                    icon: UserIcon,
                    permission: PermissionGroup.CLIENTS,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('CLIENTS'),
                            id: 'clients_list',
                            route: '/clients/list',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_DEBTS'),
                            id: 'clients_debts',
                            route: '/clients/debts',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_DEBT_OPERATIONS'),
                            id: 'clients_payments',
                            route: '/clients/payments',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_STATUSES'),
                            id: 'clients_statuses',
                            route: '/clients/statuses',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENT_STATUSES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_LEAD_QUALITY'),
                            id: 'clients_lead_quality',
                            route: '/clients/lead_quality',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENT_LEAD_QUALITY,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_BLACKLIST'),
                            id: 'clients_blacklists',
                            route: '/clients/blacklists',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENT_BLACKLIST,
                        },
                    ]
                },
                {
                    // label: this.dictionary.getSidebarLabel('CLIENTS'),
                    label: 'CRM Модуль',
                    id: 'crm-dropdown',
                    icon: CrmModuleIcon,
                    permission: PermissionGroup.CLIENTS,
                    children: [
                        {
                            //    label: this.dictionary.getSidebarLabel('CAMPAIGNS'),
                            label: 'Статистика',
                            id: 'crm_statistics',
                            route: '/crm/statistics',
                            icon: BulletPoint,
                            permission: PermissionGroup.CRM_STATISTICS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_INTERACTIONS'),
                            id: 'crm_interactions',
                            route: '/crm/interactions',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENT_INTERACTIONS
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CLIENT_ISSUES'),
                            id: 'crm_issues',
                            route: '/crm/issues',
                            icon: BulletPoint,
                            permission: PermissionGroup.CLIENT_ISSUES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('CAMPAIGNS'),
                            id: 'crm_campaigns',
                            route: '/crm/campaigns',
                            icon: BulletPoint,
                            permission: PermissionGroup.CAMPAIGNS,
                        },
                    ]
                },
                {
                    label: this.dictionary.getSidebarLabel('REPORTS'),
                    id: 'reports-dropdown',
                    icon: ReportsIcon,
                    permission: PermissionGroup.REPORTS,
                    children: [
                        {
                            label: 'Сводка',
                            id: 'reports_summary',
                            route: '/summary',
                            icon: BulletPoint,
                            permission: PermissionGroup.REPORTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('SALES REPORTS'),
                            id: 'reports_sales',
                            route: '/reports/sales',
                            icon: BulletPoint,
                            permission: PermissionGroup.REPORTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('FINANCIAL REPORTS'),
                            id: 'reports_financial',
                            route: '/reports/financial',
                            icon: BulletPoint,
                            permission: PermissionGroup.REPORTS,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('STAFF'),
                    id: 'staff-dropdown',
                    permission: PermissionGroup.ROLES,
                    icon: RolesIcon,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('ROLES'),
                            id: 'roles',
                            route: '/roles',
                            icon: BulletPoint,
                            permission: PermissionGroup.ROLES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('USERS'),
                            id: 'users',
                            route: '/users',
                            icon: BulletPoint,
                            permission: PermissionGroup.USERS,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('EXPENSES'),
                    id: 'expenses-dropdown',
                    permission: PermissionGroup.EXPENSES,
                    icon: DollarIcon,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('EXPENSE OPERATIONS'),
                            id: 'expenses_operations',
                            route: '/expenses/operations',
                            icon: BulletPoint,
                            permission: PermissionGroup.EXPENSES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('PLANNED EXPENSES'),
                            id: 'expenses_list',
                            route: '/expenses/list',
                            icon: BulletPoint,
                            permission: PermissionGroup.EXPENSES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('EXPENSE CATEGORIES'),
                            id: 'expenses_categories',
                            route: '/expenses/categories',
                            icon: BulletPoint,
                            permission: PermissionGroup.EXPENSES,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('PAYROLLS'),
                    id: 'payrolls-dropdown',
                    permission: PermissionGroup.PAYROLLS,
                    icon: GetCash,
                    children: [
                        {
                            label: this.dictionary.getSidebarLabel('PAYROLL PAYMENTS'),
                            id: 'payrolls_payments',
                            route: '/payrolls/payments',
                            icon: BulletPoint,
                            permission: PermissionGroup.PAYROLLS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('EMPLOYEE ALLOWANCES'),
                            id: 'payrolls_employee_allowances',
                            route: '/payrolls/employee/allowances',
                            icon: BulletPoint,
                            permission: PermissionGroup.PAYROLLS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('EMPLOYEE DEDUCTIONS'),
                            id: 'payrolls_employee_deductions',
                            route: '/payrolls/employee/deductions',
                            icon: BulletPoint,
                            permission: PermissionGroup.PAYROLLS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('ALLOWANCES'),
                            id: 'payrolls_allowances',
                            route: '/payrolls/allowances',
                            icon: BulletPoint,
                            permission: PermissionGroup.PAYROLLS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('DEDUCTIONS'),
                            id: 'payrolls_deductions',
                            route: '/payrolls/deductions',
                            icon: BulletPoint,
                            permission: PermissionGroup.PAYROLLS,
                        },
                    ],
                },
                {
                    //label: this.dictionary.getSidebarLabel('EXPENSES'),
                    label: 'Рассрочки',
                    id: 'installments-dropdown',
                    permission: PermissionGroup.INSTALLMENTS,
                    icon: Installment,
                    hidden: !AppStore.businessSettings?.installments,
                    children: [
                        {
                            label: 'Выданные рассрочки',
                            id: 'installments_list',
                            route: '/installments/list',
                            icon: BulletPoint,
                            permission: PermissionGroup.INSTALLMENTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('INSTALLMENT_LINES'),
                            id: 'installments_lines',
                            route: '/installments/lines',
                            icon: BulletPoint,
                            permission: PermissionGroup.INSTALLMENT_LINES,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('INSTALLMENT_CARDS'),
                            id: 'installments_cards',
                            route: '/installments/cards',
                            icon: BulletPoint,
                            permission: PermissionGroup.INSTALLMENT_CARDS,
                        },
                        {
                            label: 'Кешбек рассрочек',
                            id: 'installments_cashback',
                            route: '/installments/cashback',
                            icon: BulletPoint,
                            hidden: AppStore.userData?.role_id !== 1 && !hasPermission(AppStore.permissions || [], PermissionGroup.INSTALLMENT_CASHBACK, 'READ'),
                        },
                        {
                            label: 'Платежи Alif',
                            id: 'installments_alif_payments',
                            route: '/installments/alif-payments',
                            icon: BulletPoint,
                            // Страница осталась доступной по прямой ссылке (кнопка
                            // "Операции" на карточке Alif в /settings/integrations),
                            // но в сайдбаре Рассрочек больше не дублируется.
                            hidden: true,
                        },
                    ],
                },
                {
                    label: 'Взаиморасчёты между компаниями',
                    id: 'delegated_services_settlements',
                    route: '/delegated-services/settlements',
                    icon: MoneyBillIcon,
                    hidden: !AppStore.businessSettings?.delegated_services
                        || (AppStore.userData?.role_id !== 1 && !hasPermission(AppStore.permissions || [], PermissionGroup.TRANSACTIONS, 'READ')),
                },
                {
                    label: "Онлайн магазин",
                    id: 'online-shop',
                    permission: PermissionGroup.EXCHANGE_RATES,
                    hidden: !AppStore.businessSettings?.has_online_shop,
                    icon: OnlineShopIcon,
                    children: [
                        {
                            label: 'Товары',
                            id: 'online-shop-products',
                            route: '/online_shop/products',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCTS,
                        },
                        {
                            label: 'Категории',
                            id: 'online-shop-categories',
                            route: '/online_shop/categories',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCTS,
                        },
                        {
                            label: 'Бренды',
                            id: 'online-shop-brands',
                            route: '/online_shop/brands',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCTS,
                        },
                        {
                            label: 'Главная страница',
                            id: 'online-shop-main',
                            route: '/online_shop/main',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCTS,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('BANNERS'),
                            id: 'online-shop_banners',
                            route: '/online_shop/banners',
                            icon: BulletPoint,
                            permission: PermissionGroup.BANNERS,
                        },
                        {
                            label: 'Настройки',
                            id: 'online-shop-settings',
                            route: '/online_shop/settings',
                            icon: BulletPoint,
                            permission: PermissionGroup.PRODUCTS,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('SETTINGS'),
                    id: 'settings-dropdown',
                    permission: PermissionGroup.STATUSES,
                    icon: Settings,
                    children: [
                        {
                            label: 'Настройки бизнеса',
                            //label: this.dictionary.getSidebarLabel('STATUSES'),
                            id: 'settings_business',
                            route: '/settings/business',
                            icon: BulletPoint,
                            hidden: !(AppStore.userData?.role_id === 1),
                        },
                        {
                            label: 'Подписка',
                            id: 'settings_subscription',
                            route: '/subscription',
                            icon: BulletPoint,
                            hidden: !ENABLE_BILLING || !(AppStore.userData?.role_id === 1),
                        },
                        {
                            label: this.dictionary.getSidebarLabel('EXCHANGE_RATES'),
                            id: 'settings_exchange_rates',
                            route: '/settings/exchange_rates',
                            icon: BulletPoint,
                            permission: PermissionGroup.EXCHANGE_RATES,
                            hidden: !AppStore.businessSettings?.multicurrency,
                        },
                        {
                            label: this.dictionary.getSidebarLabel('STATUSES'),
                            id: 'settings_statuses',
                            route: '/settings/statuses',
                            icon: BulletPoint,
                            permission: PermissionGroup.STATUSES,
                        },
                    ],
                },
                {
                    label: this.dictionary.getSidebarLabel('OWNER_INTEGRATIONS'),
                    id: 'settings_integrations',
                    route: '/settings/integrations',
                    icon: IntegrationsIcon,
                    hidden: AppStore.userData?.role_id !== 1,  // Используем hidden вместо permission
                },
            ];
        })

    }
}

export default new LinksStore();
